// Query hooks (split out of the v1/v2 api/queries.ts by the v3 web foundation).
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

export const qk = {
  me: ["me"] as const,
  org: ["org"] as const,
  members: ["members"] as const,
  invitations: ["invitations"] as const,
  invitation: (token: string) => ["invitation", token] as const,
  activity: (contractId: string | null) => ["activity", contractId] as const,
  dashboard: ["dashboard"] as const,
  contracts: (q: string) => ["contracts", q] as const,
  contract: (id: string) => ["contract", id] as const,
  tor: (id: string) => ["tor", id] as const,
  torDocument: (id: string) => ["tor", id, "document"] as const,
  torVersion: (id: string, version: number) => ["tor", id, "version", version] as const,
};

/** Lists, the dashboard and the activity log may all have changed after a write. */
export function useInvalidateWork() {
  const qc = useQueryClient();
  return (contractId?: string) => {
    void qc.invalidateQueries({ queryKey: ["contracts"] });
    void qc.invalidateQueries({ queryKey: qk.dashboard });
    void qc.invalidateQueries({ queryKey: ["activity"] });
    if (contractId) void qc.invalidateQueries({ queryKey: qk.contract(contractId) });
  };
}

export type Params = Record<string, string | undefined>;

export const qk2 = {
  expertPool: (p: Params) => ["expert-pool", p] as const,
  poolExpert: (id: string) => ["pool-expert", id] as const,
  timesheets: (contractId: string, month: string) => ["timesheets", contractId, month] as const,
  events: (p: Params) => ["events", p] as const,
  event: (id: string) => ["event", id] as const,
  contacts: (p: Params) => ["contacts", p] as const,
  contact: (id: string) => ["contact", id] as const,
  stakeholderOrgs: (p: Params) => ["stakeholder-orgs", p] as const,
  deliverables: (p: Params) => ["deliverables", p] as const,
  logframe: (contractId: string) => ["logframe", contractId] as const,
  report: (id: string) => ["report", id] as const,
  document: (kind: string, id: string, query: Params) => ["document", kind, id, query] as const,
  analytics: (p: Params) => ["analytics", p] as const,
  publicForm: (token: string) => ["public-form", token] as const,
};

export const inv = (qc: QueryClient, ...keys: (readonly unknown[])[]) => {
  for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
};

/** Anything that changes a figure on a contract, a ToR or the dashboard. */
export const invWork = (qc: QueryClient, contractId?: string | null) => {
  inv(qc, ["dashboard"], ["tor"], ["activity"], ["contracts"], ["analytics"], ["document"]);
  if (contractId) inv(qc, ["contract", contractId]);
  else inv(qc, ["contract"]);
};
